"use client";

import { BookADemoModal } from "@/app/(public)/shared/components/BookADemoModal";
import { UnifiedModal, useModalManager } from "@/app/(public)/shared/components/UnifiedModal";
import "@/app/(public)/shared/tokens/colors.css";
import "@/app/(public)/shared/tokens/typography.css";
import { Button } from "@/app/(public)/shared/ui/Button";
import { motion } from "framer-motion";
import { useEffect, useState, useRef } from "react";
import { useInView } from "react-intersection-observer";
import Lottie from "lottie-react";
import { LottieRefCurrentProps } from "lottie-react";
import registrationAnimation from "@/app/(public)/shared/assets/animations/registration.json";
import budgetAnimation from "@/app/(public)/shared/assets/animations/budget.json";
import simplifiedVendorAnimation from "@/app/(public)/shared/assets/animations/simplified-vendor.json";
import expertSupportAnimation from "@/app/(public)/shared/assets/animations/expert-support.json";
import Link from "next/link";

interface FeatureCardProps {
  title: string;
  description: string;
  delay: number;
  inView: boolean;
  animationData: any;
}

const FeatureCard = ({ title, description, delay, inView, animationData }: FeatureCardProps) => {
  const lottieRef = useRef<LottieRefCurrentProps>(null);
  const { ref: cardRef, inView: cardInView } = useInView({
    threshold: 0.3,
    triggerOnce: false,
  });

  useEffect(() => {
    if (cardInView) {
      lottieRef.current?.play();
    } else {
      lottieRef.current?.pause();
    }
  }, [cardInView]);

  return (
    <motion.div
      ref={cardRef}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{ duration: 0.5, delay }}
      className="w-full rounded-xl md:rounded-2xl flex flex-col justify-between items-start p-6 md:p-8 md:h-[480px] bg-landing-secondary gap-4"
    >
      <div className="flex flex-col justify-start items-start gap-1 max-w-[360px]">
        <div className="text-tag-lg text-landing-primary">{title}</div>
        <div className="text-body text-landing-secondary">{description}</div>
      </div>
      <div className="flex justify-end items-end w-full">
        <div className="md:h-[280px] w-auto rounded-[16px] overflow-hidden">
          <Lottie
            lottieRef={lottieRef}
            animationData={animationData}
            loop={true}
            autoplay={false}
            style={{ width: "100%", height: "100%" }}
          />
        </div>
      </div>
    </motion.div>
  );
};

export const Feature4 = () => {
  const { openModal, closeModal, isModalOpen } = useModalManager();
  const { ref, inView: inViewRef } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (inViewRef) {
      const timer = setTimeout(() => {
        setInView(true);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [inViewRef]);

  const features = [
    {
      title: "Complete budget control",
      description:
        "Track expenses, forecast costs, and stay on budget with real-time insights and alerts across every event category.",
      animationData: budgetAnimation,
    },
    {
      title: "Registration made simple",
      description:
        "Build beautiful, secure registration pages in minutes. Collect payments, manage attendees, and track RSVPs—all in one place.",
      animationData: registrationAnimation,
    },
    {
      title: "Simplified vendor management",
      description:
        "Work with multiple suppliers, but only deal with one contract and one invoice. We handle all vendor coordination behind the scenes.",
      animationData: simplifiedVendorAnimation,
    },
    {
      title: "Expert support every step",
      description:
        "Our event specialists act as an extension of your team, guiding you from initial planning to on-site execution.",
      animationData: expertSupportAnimation,
    },
  ];

  return (
    <div className="w-full relative py-12 md:py-24 flex justify-center">
      <div className="w-full inline-flex flex-col justify-start items-center gap-8 md:gap-12 md:px-0" ref={ref}>
        {/* Top Headings */}
        <div className="flex flex-col gap-6 md:gap-8 w-full">
          <div className="flex flex-col justify-start items-start gap-4">
            <div className="text-h2 max-w-[530px] text-landing-primary">
              One platform replaces your entire planning stack
            </div>
            <div className="max-w-[530px] text-body-lg text-landing-secondary">
              More than just venue sourcing—Nowadays manages the full event lifecycle from planning to execution.
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 mt-2">
            <Link href="/create">
              <Button variant="primary" color="primary" size="md">
                Try our platform
              </Button>
            </Link>
            <Button variant="ghost" color="neutral" size="md" onClick={() => openModal("demo")}>
              Book a Demo
            </Button>
          </div>
        </div>

        {/* Features Grid - 2x2 Layout */}
        <div className="self-stretch flex flex-col gap-4">
          {/* Mobile: Single column, Desktop: First Row: budget control (narrow) + registration (wide) */}
          <div className="flex flex-col md:flex-row gap-4">
            <div className="md:flex-2">
              <FeatureCard
                title={features[0].title}
                description={features[0].description}
                delay={0}
                inView={inView}
                animationData={features[0].animationData}
              />
            </div>
            <div className="md:flex-3">
              <FeatureCard
                title={features[1].title}
                description={features[1].description}
                delay={0.1}
                inView={inView}
                animationData={features[1].animationData}
              />
            </div>
          </div>

          {/* Mobile: Continue single column, Desktop: Second Row: simplified vendor (wide) + expert support (narrow) */}
          <div className="flex flex-col md:flex-row gap-4">
            <div className="md:flex-3">
              <FeatureCard
                title={features[2].title}
                description={features[2].description}
                delay={0.2}
                inView={inView}
                animationData={features[2].animationData}
              />
            </div>
            <div className="md:flex-2">
              <FeatureCard
                title={features[3].title}
                description={features[3].description}
                delay={0.3}
                inView={inView}
                animationData={features[3].animationData}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Unified Modals */}
      <BookADemoModal isOpen={isModalOpen("demo")} onClose={() => closeModal("demo")} />

      <UnifiedModal
        type="content"
        isOpen={isModalOpen("video")}
        onClose={() => closeModal("video")}
        title="Watch Demo Video"
        subtitle="See Nowadays in action"
        size="lg"
        closeOnBackdrop={true}
        closeOnEscape={true}
      >
        <div className="p-4">
          <iframe
            className="w-full h-[70vh] rounded-xl"
            src="https://www.youtube.com/embed/SS2bcogWkeE"
            title="Nowadays - AI for Event Planning (YC S23)"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          ></iframe>
        </div>
      </UnifiedModal>
    </div>
  );
};
